'use client';

import { motion, useMotionValue, useReducedMotion, useSpring, useTransform } from 'motion/react';
import { useEffect } from 'react';

const MAX_TILT_DEG = 7;
const SPRING = { stiffness: 120, damping: 18, mass: 0.6 };

interface IPhoneFrameTiltProps {
  children: React.ReactNode;
}

export function PhoneFrameTilt({ children }: IPhoneFrameTiltProps): React.JSX.Element {
  const reduced = useReducedMotion();
  const px = useMotionValue(0.5);
  const py = useMotionValue(0.5);
  const sx = useSpring(px, SPRING);
  const sy = useSpring(py, SPRING);
  const rotateY = useTransform(sx, [0, 1], [-MAX_TILT_DEG, MAX_TILT_DEG]);
  const rotateX = useTransform(sy, [0, 1], [MAX_TILT_DEG, -MAX_TILT_DEG]);

  useEffect(() => {
    if (reduced) {
      return;
    }
    function onMove(e: PointerEvent): void {
      if (e.pointerType !== 'mouse') {
        return;
      }
      px.set(e.clientX / window.innerWidth);
      py.set(e.clientY / window.innerHeight);
    }
    function onLeave(): void {
      px.set(0.5);
      py.set(0.5);
    }
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerleave', onLeave);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerleave', onLeave);
    };
  }, [px, py, reduced]);

  if (reduced) {
    return <div style={{ perspective: 1000 }}>{children}</div>;
  }

  return (
    <div style={{ perspective: 1000 }}>
      <motion.div
        style={{ rotateX, rotateY, transformStyle: 'preserve-3d', willChange: 'transform' }}
      >
        {children}
      </motion.div>
    </div>
  );
}
